"use client";

import { c as _c } from "react/compiler-runtime";
import { isSlowModeCoolingDown } from "@/helpers/waves/slow-mode.helpers";
import { useEffect, useMemo, useRef, useState } from "react";
enum SubmissionStatus {
  NOT_STARTED = "NOT_STARTED",
  ACTIVE = "ACTIVE",
  ENDED = "ENDED",
}
export enum SubmissionRestriction {
  NOT_LOGGED_IN = "NOT_LOGGED_IN",
  PROXY_USER = "PROXY_USER",
  NO_PERMISSION = "NO_PERMISSION",
  NOT_STARTED = "NOT_STARTED",
  ENDED = "ENDED",
  MAX_DROPS_REACHED = "MAX_DROPS_REACHED",
}
export enum ChatRestriction {
  NOT_LOGGED_IN = "NOT_LOGGED_IN",
  PROXY_USER = "PROXY_USER",
  SLOW_MODE = "SLOW_MODE",
  NO_PERMISSION = "NO_PERMISSION",
  DISABLED = "DISABLED",
}
interface DropPrivilegesInput {
  readonly isLoggedIn: boolean;
  readonly isProxy: boolean;
  readonly canChat: boolean;
  readonly canDrop: boolean;
  readonly chatDisabled: boolean;
  readonly slowModeCooldownMs: number | null;
  readonly nextDropAllowed: number | null;
  readonly submissionStarts: number | null;
  readonly submissionEnds: number | null;
  readonly maxDropsCount: number | null;
  readonly identityDropsCount: number | null;
  readonly onSlowModeCooldownExpired?: (() => void) | undefined;
}
export interface DropPrivileges {
  readonly submissionRestriction: SubmissionRestriction | null;
  readonly chatRestriction: ChatRestriction | null;
}
export function useDropPrivileges(t0) {
  const $ = _c(9);
  const {
    isLoggedIn,
    isProxy,
    canChat,
    canDrop,
    chatDisabled,
    slowModeCooldownMs,
    nextDropAllowed,
    submissionStarts,
    submissionEnds,
    maxDropsCount,
    identityDropsCount,
    onSlowModeCooldownExpired
  } = t0;
  const [slowModeClockTick, setSlowModeClockTick] = useState(0);
  const notifiedExpiredSlowModeRef = useRef(null);
  let t1;
  let t2;
  if ($[0] !== canChat || $[1] !== nextDropAllowed || $[2] !== onSlowModeCooldownExpired || $[3] !== slowModeCooldownMs) {
    t1 = () => {
      if (slowModeCooldownMs === null || nextDropAllowed === null) {
        return;
      }
      const notifyExpiredSlowMode = () => {
        if (canChat || onSlowModeCooldownExpired === undefined || notifiedExpiredSlowModeRef.current === nextDropAllowed) {
          return;
        }
        notifiedExpiredSlowModeRef.current = nextDropAllowed;
        onSlowModeCooldownExpired();
      };
      const remainingMs = nextDropAllowed - Date.now();
      if (remainingMs <= 0) {
        notifyExpiredSlowMode();
        return;
      }
      const timeout = setTimeout(() => {
        setSlowModeClockTick(_temp);
        notifyExpiredSlowMode();
      }, remainingMs);
      return () => clearTimeout(timeout);
    };
    t2 = [canChat, nextDropAllowed, onSlowModeCooldownExpired, slowModeCooldownMs];
    $[0] = canChat;
    $[1] = nextDropAllowed;
    $[2] = onSlowModeCooldownExpired;
    $[3] = slowModeCooldownMs;
    $[4] = t1;
    $[5] = t2;
  } else {
    t1 = $[4];
    t2 = $[5];
  }
  useEffect(t1, t2);
  const now = Date.now();
  let submissionStatus;
  if (submissionStarts === null && submissionEnds === null) {
    submissionStatus = SubmissionStatus.ACTIVE;
  } else {
    if (submissionStarts === null && submissionEnds !== null) {
      submissionStatus = now <= submissionEnds ? SubmissionStatus.ACTIVE : SubmissionStatus.ENDED;
    } else {
      if (submissionEnds === null && submissionStarts !== null) {
        submissionStatus = now < submissionStarts ? SubmissionStatus.NOT_STARTED : SubmissionStatus.ACTIVE;
      } else {
        if (submissionStarts !== null && submissionEnds !== null) {
          if (now < submissionStarts) {
            submissionStatus = SubmissionStatus.NOT_STARTED;
          } else {
            if (now > submissionEnds) {
              submissionStatus = SubmissionStatus.ENDED;
            } else {
              submissionStatus = SubmissionStatus.ACTIVE;
            }
          }
        } else {
          submissionStatus = SubmissionStatus.NOT_STARTED;
        }
      }
    }
  }
  const hasReachedMaxDrops = maxDropsCount !== null && identityDropsCount !== null && identityDropsCount >= maxDropsCount;
  const isChatCoolingDown = isSlowModeCoolingDown({
    cooldownMs: slowModeCooldownMs,
    nextDropAllowed,
    now
  });
  let submissionRestriction = null;
  if (!isLoggedIn) {
    submissionRestriction = SubmissionRestriction.NOT_LOGGED_IN;
  } else {
    if (isProxy) {
      submissionRestriction = SubmissionRestriction.PROXY_USER;
    } else {
      if (!canDrop) {
        submissionRestriction = SubmissionRestriction.NO_PERMISSION;
      } else {
        if (hasReachedMaxDrops) {
          submissionRestriction = SubmissionRestriction.MAX_DROPS_REACHED;
        } else {
          if (submissionStatus === SubmissionStatus.NOT_STARTED) {
            submissionRestriction = SubmissionRestriction.NOT_STARTED;
          } else {
            if (submissionStatus === SubmissionStatus.ENDED) {
              submissionRestriction = SubmissionRestriction.ENDED;
            }
          }
        }
      }
    }
  }
  let chatRestriction = null;
  if (!isLoggedIn) {
    chatRestriction = ChatRestriction.NOT_LOGGED_IN;
  } else {
    if (isProxy) {
      chatRestriction = ChatRestriction.PROXY_USER;
    } else {
      if (chatDisabled) {
        chatRestriction = ChatRestriction.DISABLED;
      } else {
        if (isChatCoolingDown) {
          chatRestriction = ChatRestriction.SLOW_MODE;
        } else {
          if (!canChat) {
            chatRestriction = ChatRestriction.NO_PERMISSION;
          }
        }
      }
    }
  }
  let t3;
  if ($[6] !== chatRestriction || $[7] !== submissionRestriction) {
    t3 = {
      submissionRestriction,
      chatRestriction
    };
    $[6] = chatRestriction;
    $[7] = submissionRestriction;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  return t3;
}
function _temp(current) {
  return current + 1;
}